import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  FileText,
  KeyRound,
  UserCircle,
} from "lucide-react";
import {
  buildFullName,
  changePortalPasswordApi,
  fetchProfileRemote,
  updateProfileRemote,
  validateSignupPassword,
} from "@/lib/authApi";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";

const Account = () => {
  const navigate = useNavigate();
  const { contributorUser, isContributorAuthenticated, isLoading } =
    useAuth();

  const [current, setCurrent] = useState("");
  const [nextPw, setNextPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [show3, setShow3] = useState(false);

  const [profEmail, setProfEmail] = useState("");
  const [profFirst, setProfFirst] = useState("");
  const [profLast, setProfLast] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);

  useEffect(() => {
    if (!isLoading && !isContributorAuthenticated) {
      navigate("/login");
    }
  }, [isLoading, isContributorAuthenticated, navigate]);

  useEffect(() => {
    if (!contributorUser?.id || isLoading) {
      return;
    }
    let cancelled = false;
    (async () => {
      setProfileLoading(true);
      const { profile, error } = await fetchProfileRemote(
        contributorUser.id,
        "portal"
      );
      if (cancelled) {
        return;
      }
      setProfileLoading(false);
      if (error) {
        toastApiError(error.message);
        setProfEmail(contributorUser.email ?? "");
        setProfFirst("");
        setProfLast("");
        return;
      }
      if (profile) {
        setProfEmail(profile.email ?? contributorUser.email ?? "");
        setProfFirst(profile.first_name ?? "");
        setProfLast(profile.last_name ?? "");
      } else {
        setProfEmail(contributorUser.email ?? "");
        setProfFirst("");
        setProfLast("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contributorUser?.id, contributorUser?.email, isLoading]);

  const displayName =
    buildFullName(profFirst, profLast).trim() ||
    [profFirst, profLast].filter(Boolean).join(" ").trim();

  const submitProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contributorUser?.id) {
      return;
    }
    const email = profEmail.trim();
    if (!email) {
      toastApiError("Email is required.");
      return;
    }
    setProfileBusy(true);
    const { error } = await updateProfileRemote(
      contributorUser.id,
      {
        email,
        first_name: profFirst.trim(),
        last_name: profLast.trim(),
        full_name:
          buildFullName(profFirst, profLast).trim() || email,
      },
      "portal"
    );
    setProfileBusy(false);
    if (error) {
      toastApiError(error.message);
      return;
    }
    toast.success("Profile saved.");
  };

  const submitChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nextPw !== confirmPw) {
      toastApiError("New passwords do not match.");
      return;
    }
    const err = validateSignupPassword(nextPw);
    if (err) {
      toastApiError(err);
      return;
    }
    setBusy(true);
    const { error } = await changePortalPasswordApi({
      old_password: current,
      new_password: nextPw,
      new_password_confirm: confirmPw,
    });
    setBusy(false);
    if (error) {
      toastApiError(error.message);
      return;
    }
    toast.success("Password updated.");
    setCurrent("");
    setNextPw("");
    setConfirmPw("");
  };

  if (isLoading || !contributorUser) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
      </div>
    );
  }

  return (
    <div className="bg-background py-10">
      <div className="container max-w-lg">
        <Button
          variant="ghost"
          type="button"
          onClick={() => navigate("/")}
          className="text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to home
        </Button>

        <Card className="border-border/50 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <UserCircle className="text-primary h-6 w-6" />
              </div>
              <div>
                <CardTitle className="font-serif text-xl">
                  My account
                </CardTitle>
                <CardDescription>Contributor portal</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-muted-foreground">Signed in as</p>
            {profileLoading ? (
              <p className="text-muted-foreground">Loading profile…</p>
            ) : (
              <>
                {displayName ? (
                  <p className="font-medium">{displayName}</p>
                ) : null}
                <p
                  className={
                    displayName
                      ? "text-muted-foreground text-xs"
                      : "font-medium"
                  }
                >
                  {profEmail || contributorUser.email || "—"}
                </p>
              </>
            )}
            <Link
              to="/contribute#contributions"
              className="text-primary hover:underline mt-4 inline-flex items-center gap-2 text-sm font-medium"
            >
              <FileText className="h-4 w-4" />
              My contributions
            </Link>
          </CardContent>
        </Card>

        <Card className="border-border/50 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Your profile</CardTitle>
            <CardDescription>
              Stored with your Jain Lexicon account on the server.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submitProfile} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="acc-email">Email</Label>
                <Input
                  id="acc-email"
                  type="email"
                  value={profEmail}
                  autoComplete="email"
                  disabled
                  className="bg-muted/40 cursor-not-allowed opacity-80"
                  title="Email cannot be changed here."
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="acc-first">First name</Label>
                  <Input
                    id="acc-first"
                    type="text"
                    value={profFirst}
                    onChange={e => setProfFirst(e.target.value)}
                    autoComplete="given-name"
                    disabled={profileLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc-last">Last name</Label>
                  <Input
                    id="acc-last"
                    type="text"
                    value={profLast}
                    onChange={e => setProfLast(e.target.value)}
                    autoComplete="family-name"
                    disabled={profileLoading}
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={profileBusy || profileLoading}
                className="w-full"
              >
                {profileBusy ? "Saving…" : "Save profile"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-border/50 mb-8">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
                <KeyRound className="text-primary h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg">
                  Change password
                </CardTitle>
                <CardDescription>
                  Use your current password. Forgot it?{" "}
                  <Link
                    to="/forgot-password"
                    className="text-primary inline font-medium underline"
                  >
                    Email reset flow
                  </Link>
                  .
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={submitChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="acc-old">Current password</Label>
                <div className="relative">
                  <Input
                    id="acc-old"
                    type={show1 ? "text" : "password"}
                    value={current}
                    onChange={e => setCurrent(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="pr-11"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShow1(v => !v)}
                    className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    {show1 ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc-new">New password</Label>
                <div className="relative">
                  <Input
                    id="acc-new"
                    type={show2 ? "text" : "password"}
                    value={nextPw}
                    onChange={e => setNextPw(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="pr-11"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShow2(v => !v)}
                    className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    {show2 ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc-confirm">Confirm new password</Label>
                <div className="relative">
                  <Input
                    id="acc-confirm"
                    type={show3 ? "text" : "password"}
                    value={confirmPw}
                    onChange={e => setConfirmPw(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="pr-11"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShow3(v => !v)}
                    className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    {show3 ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? "Saving…" : "Update password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Account;
